import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  FormErrorMessage,
  useToast,
  SimpleGrid,
  HStack,
  Text,
} from '@chakra-ui/react'
import { FiSave } from 'react-icons/fi'
import apiService from '@/services/api'

interface Plan {
  id: number
  name: string
  slug: string
  description: string
  type: 'preset' | 'custom'
  is_active: boolean
  limits: {
    file_limit: number
    smart_tagging_limit: number
    smart_renaming_limit: number
    chatbot_queries_limit: number
    storage_limit_gb: number
    users_limit: number
  }
  price_monthly: number | null
  price_yearly: number | null
}

interface EditPlanModalProps {
  isOpen: boolean
  onClose: () => void
  plan: Plan
  onSuccess: () => void
}

export default function EditPlanModal({ isOpen, onClose, plan, onSuccess }: EditPlanModalProps) {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    is_active: true,
    file_limit: 1000,
    smart_tagging_limit: 100,
    smart_renaming_limit: 100,
    chatbot_queries_limit: 500,
    storage_limit_gb: 10,
    users_limit: 5,
    price_monthly: 0,
    price_yearly: 0,
  })

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        is_active: plan.is_active,
        file_limit: plan.limits.file_limit,
        smart_tagging_limit: plan.limits.smart_tagging_limit,
        smart_renaming_limit: plan.limits.smart_renaming_limit,
        chatbot_queries_limit: plan.limits.chatbot_queries_limit,
        storage_limit_gb: plan.limits.storage_limit_gb,
        users_limit: plan.limits.users_limit,
        price_monthly: plan.price_monthly || 0,
        price_yearly: plan.price_yearly || 0,
      })
    }
  }, [plan])

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required'
    }

    if (formData.file_limit <= 0) {
      newErrors.file_limit = 'Page limit must be greater than 0'
    }

    if (formData.smart_tagging_limit <= 0) {
      newErrors.smart_tagging_limit = 'Smart tagging limit must be greater than 0'
    }

    if (formData.smart_renaming_limit <= 0) {
      newErrors.smart_renaming_limit = 'Smart renaming limit must be greater than 0'
    }

    if (formData.chatbot_queries_limit <= 0) {
      newErrors.chatbot_queries_limit = 'Chatbot queries limit must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      await apiService.updatePlan(plan.id, formData)

      toast({
        title: 'Plan updated',
        description: `${formData.name} has been updated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      onSuccess()
    } catch (error: any) {
      toast({
        title: 'Error updating plan',
        description: error.response?.data?.message || 'Failed to update plan',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Plan: {plan.name}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel>Plan Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <HStack>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                  <Text fontSize="sm">{formData.is_active ? 'Active' : 'Inactive'}</Text>
                </HStack>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
              />
            </FormControl>

            <SimpleGrid columns={3} spacing={4}>
              <FormControl isInvalid={!!errors.file_limit}>
                <FormLabel>Page Limit</FormLabel>
                <NumberInput
                  value={formData.file_limit}
                  onChange={(valueString, valueNumber) =>
                    handleInputChange('file_limit', valueNumber || 0)
                  }
                  min={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage>{errors.file_limit}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.smart_tagging_limit}>
                <FormLabel>Smart Tagging</FormLabel>
                <NumberInput
                  value={formData.smart_tagging_limit}
                  onChange={(valueString, valueNumber) =>
                    handleInputChange('smart_tagging_limit', valueNumber || 0)
                  }
                  min={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage>{errors.smart_tagging_limit}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.chatbot_queries_limit}>
                <FormLabel>Chatbot Queries</FormLabel>
                <NumberInput
                  value={formData.chatbot_queries_limit}
                  onChange={(valueString, valueNumber) =>
                    handleInputChange('chatbot_queries_limit', valueNumber || 0)
                  }
                  min={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage>{errors.chatbot_queries_limit}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>Monthly Price ($)</FormLabel>
                <NumberInput
                  value={formData.price_monthly}
                  onChange={(valueString, valueNumber) =>
                    handleInputChange('price_monthly', valueNumber || 0)
                  }
                  min={0}
                  precision={2}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Users Limit</FormLabel>
                <NumberInput
                  value={formData.users_limit}
                  onChange={(valueString, valueNumber) =>
                    handleInputChange('users_limit', valueNumber || 0)
                  }
                  min={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Updating..."
          >
            Update Plan
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
